import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { ChevronRightIcon } from '@navikt/aksel-icons';
import type { PlanArea } from '../../../api/workEnvironmentTypes';
import { measureProgress } from '../followUpView';
import { areaName, LEVEL_COLORS } from '../riskView';
import { dayAndMonth } from '../surveyProcessView';
import { measuresFor } from '../planView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './FollowUp.module.css';

type Props = { areas: PlanArea[]; onOpen: (key: string) => void };

/** The plans followed up, each with where it stands, when there are several */
export const PlansOverviewCard = ({ areas, onOpen }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby="follow-up-plans-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="follow-up-plans-heading">
            {t('workEnvironmentFollowUp.overview.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentFollowUp.overview.intro')}
          </Paragraph>
        </div>
        <ul className={classes.planList}>
          {areas.map((area) => {
            const progress = measureProgress(area.plan ? measuresFor(area.plan, area.key) : []);
            const name = areaName(area, t);
            return (
              <li key={area.key}>
                <button
                  type="button"
                  className={classes.planRow}
                  aria-label={t('workEnvironmentFollowUp.overview.open', {
                    name: name.toLowerCase(),
                  })}
                  onClick={() => onOpen(area.key)}
                >
                  <span className={classes.planRowText}>
                    <span className="ds-heading" data-size="2xs">
                      {name}
                    </span>
                    <span className={classes.measureTitle}>
                      <Tag data-color={LEVEL_COLORS[area.level]} data-size="sm">
                        {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                      </Tag>
                      {!area.plan?.approved && (
                        <Tag data-color="warning" data-size="sm">
                          {t('workEnvironmentFollowUp.tag.reopened')}
                        </Tag>
                      )}
                    </span>
                    <span
                      className={`ds-paragraph ${process.subtle}`}
                      data-size="sm"
                    >
                      {[
                        t('workEnvironmentFollowUp.overview.progress', {
                          done: progress.done,
                          total: progress.total,
                        }),
                        progress.nextDue &&
                          t('workEnvironmentFollowUp.overview.nextDue', {
                            date: dayAndMonth(progress.nextDue, i18n.language),
                          }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <ChevronRightIcon
                    aria-hidden
                    className={classes.planRowIcon}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
